import { Endpoint, Entity } from '@rest-hooks/rest';
import { BaseAuthenticationResource } from '../resources/BaseAuthenticationResource';

interface EvaluateParams {
  scoreModelId: number;
  applicationNumber: string;
  applicationXml: string;
}

export interface PolicyCheckResult {
  policyRuleId: number;
  name: string;
  description: string;
  status: string;
  message: string;
  numeric: number;
  hidden: boolean;
}

interface EvaluateScoreModel {
  scoreModelId?: number;
  applicationNumber?: string;
  status?: string;
  score?: string;
  scoreModelName?: string;
  policyCheckResults: PolicyCheckResult[];
  successes: number;
  warnings: number;
  failures: number;
  evaluationDate?: Date;
}
class EvaluateScoreModelEntity extends Entity implements EvaluateScoreModel {
  readonly scoreModelId?: number = 0;
  readonly applicationNumber?: string = '';
  readonly status?: string = '';
  readonly score?: string = '';
  readonly successes: number = 0;
  readonly warnings: number = 0;
  readonly failures: number = 0;
  readonly evaluationDate?: Date = undefined;
  readonly scoreModelName?: string = '';
  readonly policyCheckResults: PolicyCheckResult[] = [];

  pk() {
    return `${this.scoreModelId}-${this.applicationNumber}`;
  }
}

async function fetchWithAuth(url: string, options: RequestInit): Promise<Response> {
  const init = BaseAuthenticationResource.getFetchInit(options);
  return fetch(url, init);
}

export const EvaluateScoreModel = new Endpoint(
  (params: EvaluateParams | null): Promise<EvaluateScoreModel | undefined> => {
    if (
      params == null ||
      !params.scoreModelId ||
      !params.applicationNumber ||
      !params.applicationXml
    ) {
      return new Promise((res, rej) => rej(undefined));
    }
    const { scoreModelId, applicationNumber, applicationXml } = params;
    return fetchWithAuth(`/api/scoremodels/${scoreModelId}/evaluate`, {
      method: 'POST',
      body: JSON.stringify({
        applicationNumber: applicationNumber,
        applicationXmlString: applicationXml
      }),
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then((res) => res.text())
      .then((result) => {
        // Parse the incoming XML
        const parser = new DOMParser();
        const xml = parser.parseFromString(result, 'application/xml');

        // Get the neccessary elements out of the XML
        const scoreModelElement = xml.querySelector('scorecard');
        const status = scoreModelElement?.attributes?.getNamedItem('status')?.nodeValue;
        const score = scoreModelElement?.attributes?.getNamedItem('score')?.nodeValue;
        const scoreModelName = scoreModelElement?.attributes?.getNamedItem('scoreModel')?.nodeValue;
        const policyCheckResults: PolicyCheckResult[] = [];

        // Add all of the results to policyCheckResults
        xml.querySelectorAll('PolicyCheckResult').forEach((policyCheckResultElement) => {
          const policyCheckResult: PolicyCheckResult = {
            policyRuleId:
              Number(policyCheckResultElement.querySelector('ruleid')?.textContent) ?? 0,
            name: policyCheckResultElement.querySelector('name')?.textContent ?? '',
            description: policyCheckResultElement.querySelector('description')?.textContent ?? '',
            status: policyCheckResultElement.querySelector('status')?.textContent ?? '',
            message: policyCheckResultElement.querySelector('message')?.textContent ?? '',
            numeric: Number(policyCheckResultElement.querySelector('numeric')?.textContent) ?? 0,
            hidden: Boolean(policyCheckResultElement.querySelector('hidden')?.textContent)
          };
          policyCheckResults.push(policyCheckResult);
        });

        const successes = policyCheckResults.filter((result) => result.status === 'green').length;
        const warnings = policyCheckResults.filter((result) => result.status === 'yellow').length;
        const failures = policyCheckResults.filter((result) => result.status === 'red').length;
        return {
          ...params,
          status: status ?? '',
          score: score ?? '',
          scoreModelName: scoreModelName ?? '',
          policyCheckResults: policyCheckResults,
          successes: successes,
          warnings: warnings,
          failures: failures,
          evaluationDate: new Date(Date.now())
        } as EvaluateScoreModel;
      });
  },
  {
    schema: EvaluateScoreModelEntity
  }
);
